import type { ReactElement } from "react";
import { LoadingSpinner } from "../../../../shared/components/loading/LoadingSpinner";
import { ValidationWarningIndicator } from "../../components/ValidationWarningIndicator";
import { useImageReadUrl } from "../../hooks/useImageReadUrl";

interface ImagePreviewProps {
  value: string;
  label: string;
  type: "poster" | "backdrop" | "logo" | "horizontal-tile" | "vertical-tile";
  submissionId: string | null;
  onRemove: () => void;
  mode?: "submission" | "title"; // Which entity type we're editing
  validationWarnings?: string[]; // Optional validation warnings to display
}

/**
 * Image Preview Component
 * Handles both regular URLs (TMDB, external) and R2 keys (r2://key format)
 */
export function ImagePreview({
  value,
  label,
  type,
  submissionId,
  onRemove,
  mode = "submission",
  validationWarnings,
}: Readonly<ImagePreviewProps>): ReactElement {
  // Check if value is an R2 key (format: r2://key)
  const isR2Key = value.startsWith("r2://");
  const r2Key = isR2Key ? value.substring(5) : null; // Remove "r2://" prefix

  // Fetch presigned read URL for R2 keys
  const { readUrl, loading, error } = useImageReadUrl({
    ...(mode === "title" ? { titleId: submissionId } : { submissionId }),
    key: r2Key,
    mode,
  });

  // Determine which URL to display
  const displayUrl = isR2Key ? readUrl : value;

  const getImageClass = () => {
    if (type === "poster") return "max-h-[700px] object-cover mx-auto";
    if (type === "backdrop") return "w-full aspect-[16/9] object-cover";
    return "max-h-48 object-contain mx-auto";
  };

  const getContainerClass = () => {
    // Don't use w-full for poster/logo to allow proper containment
    if (type === "poster" || type === "logo" || type === "vertical-tile") {
      return "h-auto flex items-center justify-center";
    }
    return "w-full h-auto";
  };

  if (loading) {
    return (
      <div className="relative bg-neutral-900 border border-neutral-800 rounded-lg overflow-hidden p-8">
        <div className="flex items-center justify-center">
          <LoadingSpinner size="sm" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative bg-neutral-900 border border-red-800 rounded-lg overflow-hidden p-4">
        <p className="text-sm text-red-400">Error loading image: {error}</p>
        <button
          onClick={onRemove}
          className="mt-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
        >
          Remove
        </button>
      </div>
    );
  }

  if (!displayUrl) {
    return (
      <div className="relative bg-neutral-900 border border-neutral-800 rounded-lg overflow-hidden p-4">
        <p className="text-sm text-neutral-400">No image URL available</p>
      </div>
    );
  }

  return (
    <div className="relative bg-neutral-900 border border-neutral-800 rounded-lg overflow-hidden">
      <img src={displayUrl} alt={label} className={`${getContainerClass()} ${getImageClass()}`} />

      {/* Validation warning indicator - bottom left corner */}
      {validationWarnings && validationWarnings.length > 0 && (
        <ValidationWarningIndicator warnings={validationWarnings} />
      )}

      {/* Remove button - top right corner */}
      <button
        onClick={onRemove}
        className="absolute top-2 right-2 p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors"
        title="Remove image"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
